/**
 * Biometric authentication utilities.
 * Uses expo-local-authentication for Face ID / Touch ID / Fingerprint
 * and expo-secure-store for secure token storage.
 */

import * as LocalAuthentication from 'expo-local-authentication';
import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';

const BIOMETRIC_ENABLED_KEY = 'duel_biometric_enabled';
const AUTH_TOKEN_KEY = 'duel_auth_token';
const AUTH_REFRESH_TOKEN_KEY = 'duel_auth_refresh_token';
const LAST_EMAIL_KEY = 'duel_last_email';

/** Check if the device supports biometric authentication */
export async function isBiometricAvailable(): Promise<boolean> {
  try {
    const compatible = await LocalAuthentication.hasHardwareAsync();
    if (!compatible) return false;
    const enrolled = await LocalAuthentication.isEnrolledAsync();
    return enrolled;
  } catch {
    return false;
  }
}

/** Get a human-readable label for the biometric type (Face ID, Fingerprint, etc.) */
export async function getBiometricLabel(): Promise<string> {
  try {
    const types = await LocalAuthentication.supportedAuthenticationTypesAsync();
    if (types.includes(LocalAuthentication.AuthenticationType.FACIAL_RECOGNITION)) {
      return Platform.OS === 'ios' ? 'Face ID' : 'Face Recognition';
    }
    if (types.includes(LocalAuthentication.AuthenticationType.FINGERPRINT)) {
      return Platform.OS === 'ios' ? 'Touch ID' : 'Fingerprint';
    }
    if (types.includes(LocalAuthentication.AuthenticationType.IRIS)) {
      return 'Iris';
    }
    return 'Biometric Login';
  } catch {
    return 'Biometric Login';
  }
}

/** Prompt the user for biometric authentication */
export async function authenticateWithBiometrics(): Promise<boolean> {
  try {
    const result = await LocalAuthentication.authenticateAsync({
      promptMessage: 'Authenticate to sign in',
      cancelLabel: 'Cancel',
      disableDeviceFallback: false,
      fallbackLabel: 'Use Passcode',
    });
    return result.success;
  } catch {
    return false;
  }
}

/** Check if biometric login is enabled by the user */
export async function isBiometricLoginEnabled(): Promise<boolean> {
  try {
    const val = await SecureStore.getItemAsync(BIOMETRIC_ENABLED_KEY);
    return val === 'true';
  } catch {
    return false;
  }
}

/** Enable or disable biometric login */
export async function setBiometricLoginEnabled(enabled: boolean): Promise<void> {
  try {
    if (enabled) {
      await SecureStore.setItemAsync(BIOMETRIC_ENABLED_KEY, 'true');
    } else {
      await SecureStore.deleteItemAsync(BIOMETRIC_ENABLED_KEY);
      await SecureStore.deleteItemAsync(AUTH_TOKEN_KEY);
    }
  } catch {
    // Secure store not available (web)
  }
}

/** Store auth credentials securely for biometric re-auth */
export async function storeAuthToken(accessToken: string, refreshToken?: string): Promise<void> {
  try {
    await SecureStore.setItemAsync(AUTH_TOKEN_KEY, accessToken);
    if (refreshToken) {
      await SecureStore.setItemAsync(AUTH_REFRESH_TOKEN_KEY, refreshToken);
    }
  } catch {
    // Secure store not available (web)
  }
}

/** Retrieve stored auth token */
export async function getStoredAuthToken(): Promise<string | null> {
  try {
    return await SecureStore.getItemAsync(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

/** Retrieve stored refresh token */
export async function getStoredRefreshToken(): Promise<string | null> {
  try {
    return await SecureStore.getItemAsync(AUTH_REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
}

/** Clear stored auth token */
export async function clearAuthToken(): Promise<void> {
  try {
    await SecureStore.deleteItemAsync(AUTH_TOKEN_KEY);
    await SecureStore.deleteItemAsync(AUTH_REFRESH_TOKEN_KEY);
  } catch {
    // Secure store not available (web)
  }
}

/** Store last signed-in email */
export async function storeLastEmail(email: string): Promise<void> {
  try {
    await SecureStore.setItemAsync(LAST_EMAIL_KEY, email);
  } catch {}
}

/** Get last signed-in email */
export async function getLastEmail(): Promise<string | null> {
  try {
    return await SecureStore.getItemAsync(LAST_EMAIL_KEY);
  } catch {
    return null;
  }
}

/**
 * Full biometric sign-in flow:
 * 1. Check if biometric is available & enabled
 * 2. Prompt for biometric auth
 * 3. Return stored token if successful
 */
export async function biometricSignIn(): Promise<{ success: boolean; token: string | null; refreshToken: string | null }> {
  const available = await isBiometricAvailable();
  if (!available) return { success: false, token: null, refreshToken: null };

  const enabled = await isBiometricLoginEnabled();
  if (!enabled) return { success: false, token: null, refreshToken: null };

  const token = await getStoredAuthToken();
  if (!token) return { success: false, token: null, refreshToken: null };

  const refreshToken = await getStoredRefreshToken();

  const authenticated = await authenticateWithBiometrics();
  if (!authenticated) return { success: false, token: null, refreshToken: null };

  return { success: true, token, refreshToken };
}
